import type { EventType } from '@en3/shared';
import { api } from './client';

/**
 * 埋点上报（阶段 3 起）。
 *
 * 三条原则：
 * 1. **失败绝不能影响用户体验**：所有调用都吞错（`catch` 后静默），
 *    前端不显示"埋点失败"，也不重试 —— 统计少一条远没有打断阅读严重。
 * 2. **批量**：一次会话内的事件先攒在数组里，达到 10 条或页面隐藏时统一发一次
 *    （见 `flushEvents`），避免每查一个词就发一个请求。
 * 3. **不采集隐私**：只上报"做了什么"，不上报原文内容；`anonId` 是随机生成并存在
 *    localStorage 里的匿名标识，不关联任何个人身份。
 */

export interface PendingEvent {
  type: EventType;
  articleId?: string;
  wordId?: string;
  payload?: unknown;
}

const ANON_KEY = 'en3.anonId';

/** 匿名设备标识：随机 16 字节十六进制，只用于"同一台设备的行为聚类"。 */
export function anonId(): string {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    const value = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(ANON_KEY, value);
    return value;
  } catch {
    // 隐私模式/禁用 localStorage 时退化为"本次会话有效"的随机值
    return `tmp-${Math.random().toString(36).slice(2, 10)}`;
  }
}

let queue: PendingEvent[] = [];
const FLUSH_THRESHOLD = 10;

/** 入队；达到阈值就自动 flush。 */
export function track(event: PendingEvent): void {
  queue.push(event);
  if (queue.length >= FLUSH_THRESHOLD) void flushEvents();
}

/** 立刻发送队列（页面隐藏、文章读完等关键时刻调用）。 */
export async function flushEvents(): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.slice(0, 30);
  queue = queue.slice(batch.length);

  try {
    await api.post<{ accepted: number }>('/api/events', {
      events: batch.map((event) => ({ ...event, anonId: anonId() })),
    });
  } catch {
    // 刻意吞掉：埋点是旁路能力
  }
}

/** 页面隐藏/卸载时尽力发一次（`sendBeacon` 不需要等响应）。 */
export function installEventFlusher(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushEvents();
  });
}
